import React, { createContext, useContext, useMemo, useState } from "react";

interface PaginationFilters {
  linesPerPage: string;
  page: number;
  orderBy?: string;
  direction?: string;
}

interface ReportsFilterContextProps {
  filterOptions: any;
  setFilterOptions: React.Dispatch<React.SetStateAction<any>>;
  paginationFilters: PaginationFilters;
  setPaginationFilters: React.Dispatch<React.SetStateAction<PaginationFilters>>;
  bodyFilters: any;
  handleChangeBodyFilter: (prop: string, value: any) => void;
  handleResetBodyFilters: () => void;
  handleLinesPerPage: (linesPerPage: string) => void;
  handleChangeOrder: (order: { orderBy: string; direction: string }) => void;
  handlePageChange: (value: number) => void;
  handleSearchChange: (event: any, oqueVocePrecisa?: boolean) => void;
}

const ReportsFilterContext = createContext<
  ReportsFilterContextProps | undefined
>(undefined);

export const ReportsFilterProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [paginationFilters, setPaginationFilters] = useState<PaginationFilters>(
    { orderBy: "id", direction: "DESC", linesPerPage: "10", page: 0 }
  );

  const [bodyFilters, setBodyFilters] = useState<any>({});
  const [filterOptions, setFilterOptions] = useState<any>({});

  const handleLinesPerPage = (linesPerPage: string) => {
    setPaginationFilters((prevFilters) => ({
      ...prevFilters,
      linesPerPage,
      page: 0,
    }));
  };

  const handleChangeOrder = (order: { orderBy: string; direction: string }) => {
    setPaginationFilters((prevFilters) => ({
      ...prevFilters,
      orderBy: order.orderBy,
      direction: order.direction,
    }));
  };

  const handlePageChange = (value: number) => {
    setPaginationFilters((prevFilters) => ({
      ...prevFilters,
      page: value,
    }));
  };

  const handleChangeBodyFilter = (prop: string, value: any) => {
    setBodyFilters((prevFilters: any) => ({
      ...prevFilters,
      [prop]: value,
    }));
  };

  const handleResetBodyFilters = () => {
    setBodyFilters({});
  };

  const handleSearchChange = (event: any, oqueVocePrecisa?: boolean) => {
    const value = typeof event === "string" ? event : event?.target?.value ?? "";

    setBodyFilters((prevFilters: any) => ({
      ...prevFilters,
      [oqueVocePrecisa ? "oqueVocePrecisa" : "oqueVoceProcura"]: value,
    }));

    setPaginationFilters((prevState) => ({
      ...prevState,
      page: 0,
    }));
  };

  const value = useMemo(
    () => ({
      filterOptions,
      setFilterOptions,
      paginationFilters,
      setPaginationFilters,
      bodyFilters,
      handleChangeBodyFilter,
      handleResetBodyFilters,
      handleLinesPerPage,
      handleChangeOrder,
      handlePageChange,
      handleSearchChange,
    }),
    [filterOptions, paginationFilters, bodyFilters]
  );

  return (
    <ReportsFilterContext.Provider value={value}>
      {children}
    </ReportsFilterContext.Provider>
  );
};

export const useReportsFilter = () => {
  const context = useContext(ReportsFilterContext);
  if (!context) {
    throw new Error(
      "useReportsFilter must be used within a ReportsFilterProvider"
    );
  }
  return context;
};
